import { useEffect, useState, type FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import type {
  ThemeClasses,
  NewDeviceFormType,
  Vehicle,
  Brand,
  VehicleAssignmentMode,
  RemolqueAssignmentMode,
} from "../types";
import type { AvailableTrailer } from "../../../../../../features/devices/queries";
import { checkIdentifierAvailable } from "../../../../../../features/devices/mutations";
import Modal from "../../../../../../components/base/Modal";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useQuery } from "@tanstack/react-query";
import { fetchEquipoLookups } from "../../../../../../features/sg/equipos";
import { getCustomSelectStyles } from "../../../../shared";
import { useSchemaOverrides } from "../../../../../../hooks/useSchemaOverrides";
import useIsReseller from "../../../../../../hooks/useIsReseller";
import BrandModel from "./BrandModel";
import { VehicleInlineForm } from "./VehicleInlineForm";
import { TrailerInlineForm } from "./TrailerInlineForm";
import { PANIC_BUTTON_OPTIONS, parsePanicButton } from "../utils/panicButton";

interface DeviceAddModalProps {
  formData: NewDeviceFormType;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  availableVehicles: Vehicle[];
  availableVehiclesLoading?: boolean;
  availableTrailers: AvailableTrailer[];
  availableTrailersLoading?: boolean;
  companyId: string | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSave: (data: NewDeviceFormType) => void;
  onInputChange?: (field: string, value: any) => void;
  onRefreshVehicles?: () => void;
  allBrands: Brand[];
  allBrandsLoading: boolean;
  isSgLinked?: boolean;
}

export function DeviceAddModal({
  formData,
  themeClasses,
  isDarkMode,
  availableVehicles,
  availableVehiclesLoading = false,
  availableTrailers,
  availableTrailersLoading = false,
  isSubmitting,
  onClose,
  onSave,
  allBrands,
  isSgLinked = false,
}: DeviceAddModalProps) {
  const { t } = useTranslation("adminSections");
  const { isVisible, isRequired } = useSchemaOverrides("devices", "postDevice");
  const [checkingIdentifier, setCheckingIdentifier] = useState(false);
  const { isDisabled } = useIsReseller();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm<NewDeviceFormType>({ defaultValues: formData });

  const selectedBrandId = watch("brand_id");
  const vehicleMode = watch("vehicleMode");
  const remolqueMode = watch("remolqueMode");

  const selectedBrand = allBrands.find((b) => b.id === selectedBrandId);
  const isDumax = import.meta.env.VITE_THEME === "dumax";

  const { statuses, fetchStatuses } = useStatusesStore();

  useEffect(() => {
    if (statuses.length === 0) fetchStatuses({ limit: 100, page: 1 });
  }, [statuses.length, fetchStatuses]);

  useEffect(() => { reset(formData); }, [formData, reset]);

  const getInputClassName = (hasError: boolean) =>
    `w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border ${
      hasError ? "border-red-500" : themeClasses.cardBorder
    } rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 ${
      hasError ? "focus:ring-red-500" : "focus:ring-primary-500"
    }`;

  const errorClassName = "text-red-500 text-xs mt-1";

  const statusOptions = getStatusOptionsForEntity(statuses, "devices", t);

  const { data: sgEquipoLookups } = useQuery({
    queryKey: ["sg-equipo-lookups"],
    queryFn: fetchEquipoLookups,
    staleTime: 5 * 60 * 1000,
  });
  const watchedStatusId = watch("status_id");
  const sgConditionOptions = (() => {
    const sgStatuses = sgEquipoLookups?.statuses ?? [];
    const conditions = sgEquipoLookups?.conditions ?? [];
    const active = watchedStatusId === "R5tW0uX4yY9cF6dH2aI8";
    const sgStatus = sgStatuses.find(
      (s: any) =>
        (s.name_es || s.name || "").toLowerCase() ===
        (active ? "activo" : "inactivo"),
    );
    return conditions
      .filter((c: any) => !sgStatus || String(c.status_id) === String(sgStatus.id))
      .map((c: any) => ({ value: c.name, label: c.name }));
  })();

  const brandOptions = allBrands.map((b) => ({
    value: b.id,
    label: `${b.brand} - ${b.model} - ${b.origin?.toUpperCase()}`,
  }));
  const typeOptions = [
    { value: "gps", label: t("devices.type.gps") },
    { value: "phone", label: t("devices.type.phone") },
    { value: "bluetooth", label: t("devices.type.bluetooth") },
    { value: "internal", label: t("devices.type.internal") },
  ];
  const vehicleOptions = availableVehicles.map((v) => ({
    value: v.id,
    label: `${v.name} - ${v.plate || t("devices.messages.noPlate")}`,
  }));
  const trailerOptions = (Array.isArray(availableTrailers) ? availableTrailers : []).map((tr) => ({
    value: tr.id,
    label: `${tr.name || t("devices.messages.noName")} - ${tr.plate || t("devices.messages.noPlate")}`,
  }));

  const vehicleModes: { value: VehicleAssignmentMode; label: string }[] = [
    ...(isDumax ? [] : [{ value: "none" as const, label: t("devices.vehicleMode.none", { defaultValue: "Ninguno" }) }]),
    { value: "existing", label: t("devices.vehicleMode.existing", { defaultValue: "Existente" }) },
    { value: "new", label: t("devices.vehicleMode.new", { defaultValue: "Crear nuevo" }) },
  ];

  const remolqueModes: { value: RemolqueAssignmentMode; label: string }[] = [
    { value: "none", label: t("devices.remolqueMode.none", { defaultValue: "Ninguno" }) },
    { value: "existing", label: t("devices.remolqueMode.existing", { defaultValue: "Existente" }) },
    { value: "new", label: t("devices.remolqueMode.new", { defaultValue: "Crear nuevo" }) },
  ];

  const handleIdentifierBlur = async (e: FocusEvent<HTMLInputElement>) => {
    const trimmed = e.target.value.trim();
    if (!trimmed) { clearErrors("identifier"); return; }

    setCheckingIdentifier(true);
    try {
      const result = await checkIdentifierAvailable(trimmed);
      if (!result.available) {
        setError("identifier", {
          type: "manual",
          message: result.message || t("devices.validation.imeiAlreadyExists", { defaultValue: "Este IMEI ya está registrado" }),
        });
      } else {
        clearErrors("identifier");
      }
    } finally {
      setCheckingIdentifier(false);
    }
  };

  return (
    <Modal
      width="w-full max-w-4xl"
      content={
        <form
          id="device-add-form"
          onSubmit={handleSubmit(onSave)}
          className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 w-full"
        >
          {isSgLinked && (
            <div
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
                isDarkMode
                  ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                  : "border-amber-300 bg-amber-50 text-amber-800"
              }`}
            >
              <i className="ri-links-line mt-0.5 text-lg"></i>
              <div>
                <p className="text-sm font-semibold">
                  {t("devices.sgNotice.title", { defaultValue: "Compañía ligada a SG" })}
                </p>
                <p className="mt-0.5 text-xs">
                  {t("devices.sgNotice.create", {
                    defaultValue:
                      "Al crear este equipo también se creará en SG; el estado y la etiqueta se sincronizan.",
                  })}
                </p>
              </div>
            </div>
          )}

          <SectionTitle text={t("devices.sections.basicInfo")} themeClasses={themeClasses} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isVisible("name") && (
              <Field label={t("devices.fields.name")} required={isRequired("name")} themeClasses={themeClasses}>
                <input
                  type="text"
                  className={getInputClassName(!!errors.name)}
                  placeholder={t("devices.placeholders.name")}
                  {...register("name", { required: isRequired("name") ? t("devices.validation.nameRequired") : false })}
                />
                {errors.name && <span className={errorClassName}>{errors.name.message}</span>}
              </Field>
            )}

            {isVisible("identifier") && (
              <Field label={t("devices.fields.imei")} required={isRequired("identifier")} themeClasses={themeClasses}>
                <input
                  type="text"
                  className={getInputClassName(!!errors.identifier)}
                  placeholder={t("devices.placeholders.imei")}
                  {...register("identifier", { required: isRequired("identifier") ? t("devices.validation.imeiRequired") : false })}
                  onBlur={handleIdentifierBlur}
                />
                {checkingIdentifier && (
                  <span className={`text-xs ${themeClasses.textSecondary}`}>
                    {t("devices.validation.checkingImei", { defaultValue: "Verificando disponibilidad..." })}
                  </span>
                )}
                {errors.identifier && <span className={errorClassName}>{errors.identifier.message}</span>}
              </Field>
            )}

            {isVisible("serial") && (
              <Field label={t("devices.fields.serial")} required={isRequired("serial")} themeClasses={themeClasses}>
                <input
                  type="text"
                  className={getInputClassName(!!errors.serial)}
                  placeholder={t("devices.placeholders.serial")}
                  {...register("serial", { required: isRequired("serial") ? t("devices.validation.serialRequired") : false })}
                />
                {errors.serial && <span className={errorClassName}>{errors.serial.message}</span>}
              </Field>
            )}

            {isVisible("type") && (
              <Field label={t("devices.fields.type")} required={isRequired("type")} themeClasses={themeClasses}>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: isRequired("type") ? t("devices.validation.typeRequired") : false }}
                  render={({ field }) => (
                    <Select
                      options={typeOptions}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.type)}
                      placeholder={t("devices.placeholders.selectType")}
                      value={typeOptions.find((o) => o.value === field.value) || null}
                      onChange={(opt: any) => field.onChange(opt?.value || "")}
                      isSearchable
                      noOptionsMessage={() => t("devices.noOptions.types")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.type && <span className={errorClassName}>{errors.type.message}</span>}
              </Field>
            )}

            {isVisible("status_id") && (
              <Field label={t("devices.fields.status")} required={isRequired("status_id")} themeClasses={themeClasses}>
                <Controller
                  name="status_id"
                  control={control}
                  rules={{ required: isRequired("status_id") ? t("devices.validation.statusRequired") : false }}
                  render={({ field }) => (
                    <Select
                      options={statusOptions}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.status_id)}
                      placeholder={t("devices.placeholders.selectStatus")}
                      value={statusOptions.find((o) => o.value === field.value) || null}
                      onChange={(opt: any) => field.onChange(opt?.value || "")}
                      isSearchable
                      noOptionsMessage={() => t("devices.noOptions.statuses")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.status_id && <span className={errorClassName}>{errors.status_id.message}</span>}
              </Field>
            )}

            {isSgLinked && (
              <Field
                label={t("devices.fields.tag", { defaultValue: "Etiqueta (SG)" })}
                required={false}
                themeClasses={themeClasses}
              >
                <Controller
                  name="sg_condition"
                  control={control}
                  render={({ field }) => {
                    const opts =
                      field.value &&
                      !sgConditionOptions.some((o) => o.value === field.value)
                        ? [...sgConditionOptions, { value: field.value, label: field.value }]
                        : sgConditionOptions;
                    return (
                      <Select
                        options={opts}
                        styles={getCustomSelectStyles(isDarkMode, false)}
                        placeholder={t("devices.placeholders.selectTag", { defaultValue: "Sin etiqueta" })}
                        value={opts.find((o) => o.value === field.value) || null}
                        onChange={(opt: any) => field.onChange(opt?.value || "")}
                        isClearable
                        noOptionsMessage={() => t("devices.noOptions.statuses")}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    );
                  }}
                />
              </Field>
            )}
          </div>

          {isDumax && (
            <>
              <SectionWithTabs
                title={t("devices.fields.vehicleAssigned", { defaultValue: "Vehículo" })}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                options={vehicleModes}
                value={vehicleMode}
                onChange={(v) => setValue("vehicleMode", v as VehicleAssignmentMode)}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {vehicleMode === "existing" && (
                  <Field label={t("devices.fields.vehicleAssigned")} required themeClasses={themeClasses}>
                    <Controller
                      name="vehicle_id"
                      control={control}
                      rules={{ validate: (v) => vehicleMode !== "existing" || !!v || t("devices.validation.vehicleRequired") }}
                      render={({ field }) => (
                        <Select
                          options={vehicleOptions}
                          styles={getCustomSelectStyles(isDarkMode, !!errors.vehicle_id)}
                          placeholder={availableVehiclesLoading ? t("devices.placeholders.loadingVehicles") : t("devices.placeholders.noVehicle")}
                          value={vehicleOptions.find((o) => o.value === field.value) || null}
                          onChange={(opt: any) => field.onChange(opt?.value || "")}
                          isSearchable
                          isClearable
                          isLoading={availableVehiclesLoading}
                          isDisabled={availableVehiclesLoading}
                          noOptionsMessage={() => t("devices.noOptions.vehicles")}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      )}
                    />
                    {errors.vehicle_id && <span className={errorClassName}>{errors.vehicle_id.message}</span>}
                  </Field>
                )}

                {vehicleMode === "new" && (
                  <VehicleInlineForm
                    control={control}
                    errors={errors}
                    register={register}
                    isDarkMode={isDarkMode}
                    getInputClassName={getInputClassName}
                    setError={setError}
                    clearErrors={clearErrors}
                  />
                )}
              </div>

              <SectionWithTabs
                title={t("trailers.title", { defaultValue: "Remolque" })}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                options={remolqueModes}
                value={remolqueMode}
                onChange={(v) => setValue("remolqueMode", v as RemolqueAssignmentMode)}
                disabled={vehicleMode === "none"}
                disabledHint={t("devices.remolqueMode.requiresVehicle", { defaultValue: "Asigna un vehículo primero" })}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {remolqueMode === "existing" && (
                  <Field label={t("trailers.title", { defaultValue: "Remolque" })} required themeClasses={themeClasses}>
                    <Controller
                      name="remolque_id"
                      control={control}
                      rules={{ validate: (v) => remolqueMode !== "existing" || !!v || t("devices.validation.remolqueRequired", { defaultValue: "Selecciona un remolque" }) }}
                      render={({ field }) => (
                        <Select
                          options={trailerOptions}
                          styles={getCustomSelectStyles(isDarkMode, !!errors.remolque_id)}
                          placeholder={availableTrailersLoading ? t("devices.placeholders.loadingTrailers", { defaultValue: "Cargando..." }) : t("devices.placeholders.selectTrailer", { defaultValue: "Selecciona un remolque" })}
                          value={trailerOptions.find((o) => o.value === field.value) || null}
                          onChange={(opt: any) => field.onChange(opt?.value || "")}
                          isSearchable
                          isClearable
                          isLoading={availableTrailersLoading}
                          isDisabled={availableTrailersLoading}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      )}
                    />
                    {errors.remolque_id && <span className={errorClassName}>{errors.remolque_id.message}</span>}
                  </Field>
                )}

                {remolqueMode === "new" && (
                  <TrailerInlineForm
                    control={control}
                    errors={errors}
                    register={register}
                    isDarkMode={isDarkMode}
                    getInputClassName={getInputClassName}
                    setError={setError}
                    clearErrors={clearErrors}
                  />
                )}
              </div>
            </>
          )}

          <SectionTitle text={t("devices.sections.simConnectivity")} themeClasses={themeClasses} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isVisible("sim_card") && (
              <Field label={t("devices.fields.simCard")} required={isRequired("sim_card")} themeClasses={themeClasses}>
                <input
                  type="text"
                  className={getInputClassName(!!errors.sim_card)}
                  placeholder={t("devices.placeholders.simCard")}
                  {...register("sim_card", { required: isRequired("sim_card") ? t("devices.validation.simCardRequired") : false })}
                />
                {errors.sim_card && <span className={errorClassName}>{errors.sim_card.message}</span>}
              </Field>
            )}

            {isVisible("iccid") && (
              <Field label={t("devices.fields.iccid")} required={isRequired("iccid")} themeClasses={themeClasses}>
                <input
                  type="text"
                  className={getInputClassName(!!errors.iccid)}
                  placeholder={t("devices.placeholders.iccid")}
                  {...register("iccid", { required: isRequired("iccid") ? t("devices.validation.iccidRequired") : false })}
                />
                {errors.iccid && <span className={errorClassName}>{errors.iccid.message}</span>}
              </Field>
            )}

            {isVisible("phone_number") && (
              <Field label={t("devices.fields.phone")} required={isRequired("phone_number")} themeClasses={themeClasses}>
                <input
                  type="text"
                  className={getInputClassName(!!errors.phone_number)}
                  placeholder={t("devices.placeholders.phone")}
                  {...register("phone_number", { required: isRequired("phone_number") ? t("devices.validation.phoneRequired") : false })}
                />
                {errors.phone_number && <span className={errorClassName}>{errors.phone_number.message}</span>}
              </Field>
            )}

            {isVisible("brand_id") && (
              <Field label={t("devices.fields.brand")} required={isRequired("brand_id")} themeClasses={themeClasses}>
                <Controller
                  name="brand_id"
                  control={control}
                  rules={{ required: isRequired("brand_id") ? t("devices.validation.brandRequired") : false }}
                  render={({ field }) => (
                    <Select
                      options={brandOptions}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.brand_id)}
                      placeholder={t("devices.placeholders.selectBrand")}
                      value={brandOptions.find((o) => o.value === field.value) || null}
                      onChange={(opt: any) => field.onChange(opt?.value || "")}
                      isSearchable
                      noOptionsMessage={() => t("devices.noOptions.brands")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.brand_id && <span className={errorClassName}>{errors.brand_id.message}</span>}
              </Field>
            )}
          </div>

          {selectedBrand && (
            <BrandModel isDarkMode={isDarkMode} themeClasses={themeClasses} t={t} selectedBrand={selectedBrand} />
          )}

          <SectionTitle text={t("devices.sections.dates")} themeClasses={themeClasses} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isVisible("installation_date") && (
              <Field label={t("devices.fields.installationDate")} required={isRequired("installation_date")} themeClasses={themeClasses}>
                <input
                  type="datetime-local"
                  className={getInputClassName(!!errors.installation_date)}
                  {...register("installation_date", { required: isRequired("installation_date") ? t("devices.validation.installationDateRequired") : false })}
                />
                {errors.installation_date && <span className={errorClassName}>{errors.installation_date.message}</span>}
              </Field>
            )}

            {isVisible("activation_date") && (
              <Field label={t("devices.fields.activationDate")} required={isRequired("activation_date")} themeClasses={themeClasses}>
                <input type="datetime-local" className={getInputClassName(false)} {...register("activation_date")} />
              </Field>
            )}
          </div>

          {/* Mismo candado de hardware que el sensor de combustible en la edición. */}
          <SectionTitle text="Hardware" themeClasses={themeClasses} />
          <div className={`flex flex-col gap-1 text-sm ${themeClasses.text} ${isDisabled ? "opacity-60" : ""}`}>
            <label className="font-medium">
              ¿Tiene botón de pánico?
              <select
                className={getInputClassName(false)}
                {...register("has_panic_button", { setValueAs: parsePanicButton })}
                disabled={isDisabled}
              >
                {PANIC_BUTTON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className={`block text-xs ${themeClasses.textSecondary}`}>
                Sí: su código 5 cuenta como pánico en los informes. No: tampoco alerta en vivo.
                Sin revisar: alerta en vivo, pero no cuenta en los informes.
              </span>
            </label>
          </div>
        </form>
      }
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("devices.modal.addTitle")}
      buttonAction={
        <button
          type="submit"
          form="device-add-form"
          disabled={isSubmitting || checkingIdentifier}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {isSubmitting ? t("devices.actions.saving") : t("devices.actions.save")}
        </button>
      }
    />
  );
}

function SectionTitle({ text, themeClasses }: { text: string; themeClasses: ThemeClasses }) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-4 mt-6">
      <h3 className={`text-sm font-semibold ${themeClasses.text}`}>{text}</h3>
    </div>
  );
}

function SectionWithTabs({
  title,
  themeClasses,
  isDarkMode,
  options,
  value,
  onChange,
  disabled,
  disabledHint,
}: {
  title: string;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  disabledHint?: string;
}) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 pb-3 mb-4 mt-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h3 className={`text-sm font-semibold ${themeClasses.text}`}>{title}</h3>
        <div
          className={`inline-flex rounded-lg p-0.5 ${
            isDarkMode ? "bg-dark-card" : "bg-gray-100"
          } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                value === opt.value
                  ? "bg-primary-500 text-gray-900 shadow-sm"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {disabled && disabledHint && (
        <span className={`text-xs ${themeClasses.textSecondary} mt-1 block`}>{disabledHint}</span>
      )}
    </div>
  );
}

function Field({
  label,
  required,
  themeClasses,
  children,
}: {
  label: string;
  required?: boolean | string;
  themeClasses: ThemeClasses;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
